import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash/debounce';

import CitizenMapMobileTopBar from '@components/CitizenMapMobileTopBar';
import FollowAuthModal from '@components/FollowAuthModal';
import MapViewFiltersDialog from '@components/MapViewFiltersDialog';
import PublicMapView, { IPublicMapViewHandle } from '@components/PublicMapView';
import SubmissionCardView from '@components/SubmissionCardView';
import SubmissionFilters, {
  type ISubmissionFilters,
  DEFAULT_SUBMISSION_FILTERS,
  hasActiveSubmissionFilters,
} from '@components/SubmissionFilters';

import { AuthScreen, useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { CRM_ISSUE_ID, PENDING_FOLLOW_ISSUE_ID } from '@private-crm-app/constants';
import {
  fetchPublicMapIssues,
  fetchStaffPreviewMapConfig,
  fetchStaffPreviewPublicMapIssues,
  followPublicMapIssue,
  unfollowPublicMapIssue,
} from '@private-crm-app/requests/publicMap';
import {
  buildIssueUrl,
  clearPendingFollowIntent,
  trackPublicMapViewed,
  trackPublicMapFilterApplied,
  trackPublicMapFollowClicked,
} from '@private-crm-app/utils';

import { getCRMMapConfig } from '@actions/crm';
import { fetchProductLayout } from '@actions/productLayout';

import { IPublicMapSubmissionsApiResponse, ISubmissionPayloadItem } from './types';
import {
  getDateRangeForFilter,
  getInitialIsMobileViewport,
  isCategoriesEmpty,
  MOBILE_VIEWPORT_MEDIA_QUERY,
  toCardProps,
} from './utils';

import './style.scss';

const VIEWPORT_DEBOUNCE_MS = 300;

const DATE_RANGE_LABEL_MAP: Record<string, string> = {
  ANY_TIME: 'Reported any time',
  TODAY: 'Today',
  LAST_7: 'Last 7 days',
  LAST_30: 'Last 30 days',
  LAST_90: 'Last 90 days',
};

function resolveFilterTrackData(
  prev: ISubmissionFilters,
  next: ISubmissionFilters,
  categories: ICRMCategoryResponse[]
): { filter_type: string; filter_value: string } | null {
  if (prev.status !== next.status) {
    const labels: Record<string, string> = { OPEN: 'Open', CLOSED: 'Closed', ALL: 'All' };
    return { filter_type: 'status', filter_value: labels[next.status] ?? next.status };
  }
  if (JSON.stringify(prev.categoryIds) !== JSON.stringify(next.categoryIds)) {
    const added = next.categoryIds.find((id) => !prev.categoryIds.includes(id));
    const removed = prev.categoryIds.find((id) => !next.categoryIds.includes(id));
    const relevantId = added ?? removed;
    const categoryName = categories.find((c) => c.id === relevantId)?.name ?? String(relevantId);
    return { filter_type: 'category', filter_value: categoryName };
  }
  if (prev.dateRange !== next.dateRange) {
    return { filter_type: 'date_range', filter_value: DATE_RANGE_LABEL_MAP[next.dateRange] ?? next.dateRange };
  }
  return null;
}

export interface ICitizenMapViewProps {
  crmToken?: string | null;
  onMapConfig404?: () => void;
  /**
   * When true, calls the authenticated staff-preview endpoints
   * (`/agencies/crm/...`) which bypass the `isPublicMapEnabled` gate.
   * Used by the Submission Settings preview modal so staff can see the
   * map even before the feature is enabled for citizens.
   */
  previewMode?: boolean;
}

export default function CitizenMapView({
  crmToken: crmTokenProp,
  onMapConfig404,
  previewMode = false,
}: ICitizenMapViewProps = {}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const authContext = useAuthManagerContext();
  const isAuthenticated = !previewMode && authContext.isAuthenticated;
  const crmTokenFromRedux = useSelector((state: IReduxStore) => state.productLayout.productLayout?.crm?.token);
  const crmToken = crmTokenProp ?? crmTokenFromRedux;
  const crmConfig = useSelector((state: IReduxStore) => state.crm?.config);
  const crmError = useSelector((state: IReduxStore) => state.crm?.error);
  const productLayoutCategories = useSelector(
    (state: IReduxStore) => state.productLayout?.productLayout?.crm?.categories
  );
  const productLayoutCrmToken = useSelector((state: IReduxStore) => state.productLayout?.productLayout?.crm?.token);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const floatingCardCloseRef = useRef<HTMLButtonElement>(null);
  const [submissions, setSubmissions] = useState<ISubmissionPayloadItem[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapConfig, setMapConfig] = useState<IMapConfig | null>(null);
  const [filters, setFilters] = useState<ISubmissionFilters>(DEFAULT_SUBMISSION_FILTERS);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(getInitialIsMobileViewport);
  const [mobileActiveView, setMobileActiveView] = useState<'map' | 'list'>('map');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isFollowAuthModalOpen, setIsFollowAuthModalOpen] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const publicMapRef = useRef<IPublicMapViewHandle>(null);
  const mapConfigLoadedRef = useRef(false);
  const previewLayoutRequestedRef = useRef(false);
  const previewConfigRequestedRef = useRef(false);
  const lastBboxRef = useRef<IPublicMapBbox | null>(null);
  const requestIdRef = useRef<number>(0);
  const hasTrackedMapViewedRef = useRef(false);
  const pendingFilterTrackRef = useRef<{ filter_type: string; filter_value: string } | null>(null);
  const mapCategoriesRef = useRef<ICRMCategoryResponse[]>([]);
  const filteredSubmissionsRef = useRef<ISubmissionPayloadItem[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY);
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleMediaQueryChange);

      return () => {
        mediaQueryList.removeEventListener('change', handleMediaQueryChange);
      };
    }

    mediaQueryList.addListener(handleMediaQueryChange);

    return () => {
      mediaQueryList.removeListener(handleMediaQueryChange);
    };
  }, []);

  // Citizen path: load full CRM config (mapConfig + categories) via Redux when not yet loaded
  useEffect(() => {
    if (!previewMode && crmToken && !crmConfig?.mapConfig && !crmError) {
      dispatch(getCRMMapConfig(crmToken));
    }
  }, [crmToken, previewMode, dispatch, crmConfig?.mapConfig, crmError]);

  // Preview path: request product layout once so categories may be available
  useEffect(() => {
    if (
      previewMode &&
      !previewLayoutRequestedRef.current &&
      (!productLayoutCategories || productLayoutCategories.length === 0)
    ) {
      previewLayoutRequestedRef.current = true;
      dispatch(fetchProductLayout(true));
    }
  }, [previewMode, dispatch, productLayoutCategories]);

  // Preview path: if layout has token but no categories, fetch full CRM config (same source as citizen) for categories
  useEffect(() => {
    if (
      previewMode &&
      productLayoutCrmToken &&
      !previewConfigRequestedRef.current &&
      (!productLayoutCategories || productLayoutCategories.length === 0) &&
      isCategoriesEmpty(crmConfig?.categories)
    ) {
      previewConfigRequestedRef.current = true;
      dispatch(getCRMMapConfig(productLayoutCrmToken));
    }
  }, [previewMode, dispatch, productLayoutCrmToken, productLayoutCategories, crmConfig?.categories]);

  // Preview path: fetch map config
  useEffect(() => {
    if (!previewMode) {
      return undefined;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    fetchStaffPreviewMapConfig(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setMapConfig(data);
          mapConfigLoadedRef.current = true;
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // In preview mode we do not invoke onMapConfig404; that callback is for citizen/public path only
          setError(err?.response?.data?.message || 'Failed to load map config');
          console.error('[CitizenMapView] Failed to load map config', err);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [previewMode]);

  // Resolve map config and loading/error: citizen = from Redux, preview = from local state
  const effectiveMapConfig = previewMode ? mapConfig : crmConfig?.mapConfig ?? mapConfig;
  const configLoading = previewMode
    ? isLoading
    : Boolean(crmToken && !previewMode && !crmConfig?.mapConfig && !crmError);
  const configError = previewMode
    ? error
    : crmError && !crmConfig
    ? typeof crmError === 'string'
      ? crmError
      : (crmError as { message?: string })?.message || 'Failed to load map config'
    : null;

  useEffect(() => {
    if (effectiveMapConfig) {
      mapConfigLoadedRef.current = true;
    }
  }, [effectiveMapConfig]);

  // Bbox re-fetch: issued after viewport change (debounced) or when filters change. Server applies category + status.
  const fetchIssuesForBbox = useCallback(
    (bbox: IPublicMapBbox) => {
      if ((!previewMode && !crmToken) || !mapConfigLoadedRef.current) {
        return;
      }
      lastBboxRef.current = bbox;
      requestIdRef.current += 1;
      const reqId = requestIdRef.current;
      setError(null);

      const dateRange = getDateRangeForFilter(filters.dateRange);
      const issueFilters = {
        categoryIds: filters.categoryIds.length ? filters.categoryIds : undefined,
        status: filters.status !== 'ALL' ? filters.status : undefined,
        createdAfterDate: dateRange.createdAfterDate,
        createdBeforeDate: dateRange.createdBeforeDate,
      };
      const issuesFetch = previewMode
        ? fetchStaffPreviewPublicMapIssues(bbox, issueFilters)
        : fetchPublicMapIssues(crmToken, bbox, issueFilters);

      issuesFetch
        .then((response: IPublicMapSubmissionsApiResponse) => {
          if (reqId === requestIdRef.current) {
            setSubmissions(response.data || []);
            setCount(response.count);
            if (!previewMode) {
              const responseCount = response.data?.length ?? 0;
              if (!hasTrackedMapViewedRef.current) {
                hasTrackedMapViewedRef.current = true;
                trackPublicMapViewed(isAuthenticated, responseCount, 'Open');
              } else if (pendingFilterTrackRef.current) {
                const { filter_type, filter_value } = pendingFilterTrackRef.current;
                pendingFilterTrackRef.current = null;
                trackPublicMapFilterApplied(isAuthenticated, filter_type, filter_value, responseCount);
              }
            }
          }
        })
        .catch((err) => {
          if (!axios.isCancel(err) && reqId === requestIdRef.current) {
            pendingFilterTrackRef.current = null;
            setError(err?.response?.data?.message || 'Failed to load map issues');
            console.error('[CitizenMapView] Failed to re-fetch issues for viewport', err);
          }
        });
    },
    [crmToken, previewMode, filters, isAuthenticated]
  );

  // Re-fetch when status, category, or date range changes (use current bbox)
  useEffect(() => {
    if (lastBboxRef.current) {
      fetchIssuesForBbox(lastBboxRef.current);
    }
  }, [filters, fetchIssuesForBbox]);

  const filteredSubmissions = useMemo(
    () => [...submissions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [submissions]
  );

  const selectedSubmission = selectedId !== null ? filteredSubmissions.find((s) => s.id === selectedId) ?? null : null;

  const mapCategories = useMemo(() => {
    const raw = previewMode
      ? !isCategoriesEmpty(crmConfig?.categories)
        ? crmConfig?.categories
        : productLayoutCategories
      : crmConfig?.categories;
    const list = Array.isArray(raw)
      ? raw
      : raw &&
        typeof raw === 'object' &&
        Array.isArray((raw as { showOnListing?: ICRMCategoryResponse[] }).showOnListing)
      ? (raw as { showOnListing: ICRMCategoryResponse[] }).showOnListing
      : [];

    return list.filter((c: ICRMCategoryResponse) => Boolean(c.displayOnPublicMap));
  }, [previewMode, productLayoutCategories, crmConfig?.categories]);

  mapCategoriesRef.current = mapCategories;
  filteredSubmissionsRef.current = filteredSubmissions;

  // Scroll the selected card into view
  useEffect(() => {
    if (selectedId !== null) {
      const idx = filteredSubmissions.findIndex((s) => s.id === selectedId);
      if (idx >= 0) {
        cardRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedId, filteredSubmissions]);

  // Move focus to the close button when the floating card appears (WCAG 2.4.3)
  useEffect(() => {
    if (isMobileViewport && mobileActiveView === 'map' && selectedSubmission) {
      floatingCardCloseRef.current?.focus();
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [selectedSubmission, isMobileViewport, mobileActiveView]);

  const requestCountText = useMemo(
    () =>
      !hasActiveSubmissionFilters(filters) && count > submissions.length
        ? `${submissions.length} of ${count} Requests`
        : `${filteredSubmissions.length} Requests`,
    [filters, count, submissions.length, filteredSubmissions.length]
  );

  const fetchIssuesForBboxRef = useRef(fetchIssuesForBbox);
  fetchIssuesForBboxRef.current = fetchIssuesForBbox;
  const isMapHiddenRef = useRef(false);
  isMapHiddenRef.current = isMobileViewport && mobileActiveView === 'list';
  const handleViewportChange = useRef(
    debounce((bbox: IPublicMapBbox) => {
      if (isMapHiddenRef.current) return;
      fetchIssuesForBboxRef.current(bbox);
    }, VIEWPORT_DEBOUNCE_MS)
  ).current;

  const handleMarkerClick = useCallback((id: number) => {
    setSelectedId(id);
  }, []);

  const handleFilterClick = useCallback(() => setIsMobileFiltersOpen(true), []);

  const handleFiltersChange: React.Dispatch<React.SetStateAction<ISubmissionFilters>> = useCallback(
    (action) => {
      setFilters((prev) => {
        const next = typeof action === 'function' ? action(prev) : action;
        if (!previewMode && hasTrackedMapViewedRef.current) {
          pendingFilterTrackRef.current = resolveFilterTrackData(prev, next, mapCategoriesRef.current);
        }
        return next;
      });
    },
    [previewMode]
  );

  const handleFollowClick = useCallback(
    (submissionId: number) => {
      if (!previewMode) {
        const s = filteredSubmissionsRef.current.find((x) => x.id === submissionId);
        if (s) trackPublicMapFollowClicked(isAuthenticated, s.category.name, s.status);
      }
      if (!isAuthenticated) {
        if (crmToken) {
          localStorage.setItem(PENDING_FOLLOW_ISSUE_ID, String(submissionId));
          localStorage.setItem(CRM_ISSUE_ID, String(submissionId));
        }
        setIsFollowAuthModalOpen(true);
        return;
      }

      if (!crmToken) return;

      followPublicMapIssue(crmToken, submissionId)
        .then(({ followerCount }) => {
          setSubmissions((prev) =>
            prev.map((s) =>
              s.id === submissionId ? { ...s, isFollowing: true, followerCount } : s
            )
          );
        })
        .catch((err) => {
          console.error('[CitizenMapView] Failed to follow submission', err);
        });
    },
    [isAuthenticated, crmToken, previewMode]
  );

  const handleUnfollowClick = useCallback(
    (submissionId: number) => {
      if (!crmToken) return;

      unfollowPublicMapIssue(crmToken, submissionId)
        .then(({ followerCount }) => {
          setSubmissions((prev) =>
            prev.map((s) =>
              s.id === submissionId ? { ...s, isFollowing: false, followerCount } : s
            )
          );
        })
        .catch((err) => {
          console.error('[CitizenMapView] Failed to unfollow submission', err);
        });
    },
    [crmToken]
  );

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileFiltersOpen(false);
    }
  }, [isMobileViewport]);

  return (
    <Container fluid className="citizen-map-view">
      {isMobileViewport ? (
        <Row className="citizen-map-view__mobile-controls">
          <Col>
            <CitizenMapMobileTopBar
              activeView={mobileActiveView}
              requestCountText={requestCountText}
              onViewChange={setMobileActiveView}
              onFilterClick={handleFilterClick}
            />
          </Col>
        </Row>
      ) : (
        <Row className="citizen-map-view__top-bar">
          <Col className="citizen-map-view__top-bar-inner">
            <SubmissionFilters
              filters={filters}
              mapCategories={mapCategories}
              requestCountText={requestCountText}
              onChange={handleFiltersChange}
            />
          </Col>
        </Row>
      )}
      <Row className="citizen-map-view__layout">
        <Col
          xs={12}
          md={7}
          lg={8}
          xl={9}
          className={`citizen-map-view__map-panel${
            isMobileViewport && mobileActiveView === 'list' ? ' citizen-map-view__panel--mobile-hidden' : ''
          }`}
        >
          <PublicMapView
            ref={publicMapRef}
            mapConfig={effectiveMapConfig}
            issues={filteredSubmissions}
            onViewportChange={handleViewportChange}
            onMarkerClick={handleMarkerClick}
            selectedId={selectedId}
          />
          {isMobileViewport && mobileActiveView === 'map' && selectedSubmission && (
            <div className="citizen-map-view__floating-card" role="region" aria-label="Selected submission details">
              <button
                ref={floatingCardCloseRef}
                type="button"
                className="citizen-map-view__floating-card-close"
                onClick={() => setSelectedId(null)}
                aria-label="Close"
              >
                ×
              </button>
              <SubmissionCardView
                {...toCardProps(selectedSubmission)}
                  onViewDetails={
                    crmToken
                      ? () => {
                          const full = buildIssueUrl(selectedSubmission.id, crmToken);
                          const pathname = full.split('?')[0];
                          const search = full.includes('?') ? `?${full.split('?')[1]}` : undefined;
                          history.push({ pathname, search, state: { source: 'popup' } });
                        }
                      : undefined
                  }
                onFollowClick={() => handleFollowClick(selectedSubmission.id)}
                onUnfollowClick={() => handleUnfollowClick(selectedSubmission.id)}
              />
            </div>
          )}
        </Col>
        <Col
          xs={12}
          md={5}
          lg={4}
          xl={3}
          className={`citizen-map-view__list-panel${
            isMobileViewport && mobileActiveView === 'map' ? ' citizen-map-view__panel--mobile-hidden' : ''
          }`}
        >
          {configLoading && <div className="citizen-map-view__loading">Loading...</div>}
          {configError && <div className="citizen-map-view__error">{configError}</div>}
          {!configLoading &&
            !configError &&
            (filteredSubmissions.length === 0 ? (
              <div className="citizen-map-view__empty">No requests to show</div>
            ) : (
              filteredSubmissions.map((submission, index) => (
                <SubmissionCardView
                  key={submission.id}
                  {...toCardProps(submission)}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  isSelected={selectedId === submission.id}
                  onSelect={() => {
                    setSelectedId(submission.id);
                    publicMapRef.current?.focusIssue(submission.id);
                  }}
                  onViewDetails={
                    crmToken
                      ? () => {
                          const full = buildIssueUrl(submission.id, crmToken);
                          const pathname = full.split('?')[0];
                          const search = full.includes('?') ? `?${full.split('?')[1]}` : undefined;
                          history.push({ pathname, search, state: { source: 'list' } });
                        }
                      : undefined
                  }
                  onFollowClick={() => handleFollowClick(submission.id)}
                  onUnfollowClick={() => handleUnfollowClick(submission.id)}
                />
              ))
            ))}
        </Col>
      </Row>
      <MapViewFiltersDialog
        isOpen={isMobileFiltersOpen}
        filters={filters}
        mapCategories={mapCategories}
        onApply={(next) => {
          handleFiltersChange(next);
          setIsMobileFiltersOpen(false);
        }}
        onClose={() => setIsMobileFiltersOpen(false)}
      />
      {!previewMode && (
        <FollowAuthModal
          isOpen={isFollowAuthModalOpen}
          onClose={() => {
            clearPendingFollowIntent({ clearCrmIssueId: true });
            setIsFollowAuthModalOpen(false);
          }}
          redirectScreen={AuthScreen.ISSUE}
        />
      )}
    </Container>
  );
}
